<template>
  <header
    class="relative z-[70] flex flex-wrap lg:justify-start lg:flex-nowrap w-full py-4 bg-white/80 lg:bg-transparent"
    data-testid="app-header"
  >
    <nav class="max-w-[100rem] flex flex-wrap basis-full items-center w-full mx-auto px-4 sm:px-6 lg:px-8 sm:gap-x-4">
      <div class="flex items-center sm:gap-x-2">
        <RouterLink to="/" class="flex-none rounded-md focus:outline-none focus:opacity-80">
          <svg class="w-28 h-auto" width="116" height="32" viewBox="0 0 116 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0,1h12.7l6.7,17.3l7-17.3h12.7L25.8,27H13.1L0,1z M56.1,7.1V21h2.7c5.9,0,9.101-2.1,9.101-7
              c0-5.4-2.601-6.9-9.3-6.9H56.1L56.1,7.1z M60,27H43.5V1h16.9C72.8,1,80.5,4.9,80.5,13.5C80.4,23.2,72.4,27,60,27L60,27z M117,27
              v-6.1H97.6v-4.1h15.9v-6.1H97.6V7.1h18.7V1H85v26H117z"
              fill="currentColor"
              class="fill-vde-blau"
            />
          </svg>
        </RouterLink>
      </div>

      <div class="flex items-center lg:gap-x-1 ms-auto lg:order-3">
        <div class="ms-1 lg:hidden">
          <Button
            type="button"
            variant="outline"
            size="icon"
            class="size-9 inline-flex justify-center items-center gap-x-2 rounded-full border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
            data-testid="mobile-menu-toggle"
            :aria-label="t('vde.navigation.toggle')"
            :aria-expanded="mobileMenuOpen"
            @click="mobileMenuOpen = !mobileMenuOpen"
          >
            <svg v-if="!mobileMenuOpen" class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" x2="21" y1="6" y2="6" />
              <line x1="3" x2="21" y1="12" y2="12" />
              <line x1="3" x2="21" y1="18" y2="18" />
            </svg>
            <svg v-else class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </Button>
        </div>
      </div>

      <div
        :class="[
          'transition-all duration-300 basis-full grow ms-auto lg:block lg:w-auto lg:basis-auto lg:order-2',
          mobileMenuOpen ? 'block' : 'hidden'
        ]"
        data-testid="main-navbar"
      >
        <div class="lg:overflow-visible overflow-y-auto max-h-[75vh] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300">
          <div class="py-2 lg:py-0 flex flex-col lg:flex-row lg:items-center gap-0.5 lg:gap-1">
            <div class="grow flex flex-col lg:flex-row lg:justify-between">
              <div class="flex flex-col lg:flex-row lg:items-center">
                <!-- Home -->
                <RouterLink
                  v-if="isAuthenticated"
                  to="/"
                  class="py-2 px-3 flex items-center text-base font-medium text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                  data-testid="nav-home"
                >
                  {{ t('vde.navigation.home') }}
                </RouterLink>

                <!-- Templates dropdown -->
                <div v-if="hasPermission('Template.View')" class="relative" data-testid="nav-templates-dropdown">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    class="py-2 px-3 flex items-center justify-start text-base font-medium text-gray-800 hover:bg-gray-100 rounded-lg focus:outline-none focus:bg-gray-100"
                    :aria-expanded="templatesDropdownOpen"
                    @click.stop="toggleTemplatesDropdown"
                  >
                    {{ t('vde.navigation.templates') }}
                  </Button>
                  <div
                    v-show="templatesDropdownOpen"
                    class="absolute z-10 w-52 bg-white rounded-lg shadow-md mt-1"
                    role="menu"
                  >
                    <div class="py-1 px-1 space-y-0.5">
                      <RouterLink
                        to="/templates/submodels"
                        class="py-2 px-3 flex items-center text-base font-medium text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                        data-testid="nav-submodel-templates"
                        @click="templatesDropdownOpen = false"
                      >
                        {{ t('vde.navigation.submodels') }}
                      </RouterLink>
                    </div>
                  </div>
                </div>

                <!-- Settings dropdown -->
                <div v-if="hasAnyPermission('Users.Administration', 'Roles.Administration', 'Classifications.Administration')" class="relative" data-testid="nav-settings-dropdown">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    class="py-2 px-3 flex items-center justify-start text-base font-medium text-gray-800 hover:bg-gray-100 rounded-lg focus:outline-none focus:bg-gray-100"
                    :aria-expanded="settingsDropdownOpen"
                    @click.stop="toggleSettingsDropdown"
                  >
                    {{ t('vde.navigation.settings') }}
                  </Button>
                  <div
                    v-show="settingsDropdownOpen"
                    class="absolute z-10 w-52 bg-white rounded-lg shadow-md mt-1"
                    role="menu"
                  >
                    <div class="py-1 px-1 space-y-0.5">
                      <RouterLink
                        v-if="hasPermission('Users.Administration')"
                        to="/users"
                        class="py-2 px-3 flex items-center text-base font-medium text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                        data-testid="nav-users"
                        @click="settingsDropdownOpen = false"
                      >
                        {{ t('vde.navigation.user.administration') }}
                      </RouterLink>
                      <RouterLink
                        v-if="hasPermission('Roles.Administration')"
                        to="/roles"
                        class="py-2 px-3 flex items-center text-base font-medium text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                        data-testid="nav-roles"
                        @click="settingsDropdownOpen = false"
                      >
                        {{ t('vde.navigation.role.administration') }}
                      </RouterLink>
                      <RouterLink
                        v-if="hasPermission('Classifications.Administration')"
                        to="/classifications"
                        class="py-2 px-3 flex items-center text-base font-medium text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                        data-testid="nav-classifications"
                        @click="settingsDropdownOpen = false"
                      >
                        {{ t('vde.navigation.classifications') }}
                      </RouterLink>
                    </div>
                  </div>
                </div>

                <!-- Logout -->
                <Button
                  v-if="isAuthenticated"
                  type="button"
                  variant="ghost"
                  size="sm"
                  class="py-2 px-3 flex items-center justify-start text-base font-medium text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                  data-testid="nav-logout"
                  @click="handleLogout"
                >
                  <svg class="shrink-0 size-4 me-2" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16,17 21,12 16,7" />
                    <line x1="21" x2="9" y1="12" y2="12" />
                  </svg>
                  {{ t('vde.navigation.logout') }}
                </Button>
              </div>

              <!-- Language switcher -->
              <hr class="lg:hidden border-gray-100 my-1" />
              <div class="flex flex-wrap items-center gap-2 py-1 lg:py-0">
                <div
                  v-if="showViewToggle"
                  class="inline-flex items-center rounded-lg border border-gray-300 bg-white"
                  role="group"
                  data-testid="nav-aas-view-toggle"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    class="px-3 py-2 text-sm font-medium rounded-s-lg transition-colors"
                    :class="isDetailDisplay ? 'bg-vde-blau text-white' : 'text-gray-700 hover:bg-gray-100'"
                    data-testid="nav-aas-view-toggle-detail"
                    @click="setDisplayMode('detail')"
                  >
                    {{ t('vde.workspace.mode.reading') }}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    class="px-3 py-2 text-sm font-medium rounded-e-lg transition-colors"
                    :class="!isDetailDisplay ? 'bg-vde-blau text-white' : 'text-gray-700 hover:bg-gray-100'"
                    data-testid="nav-aas-view-toggle-view"
                    @click="setDisplayMode('view')"
                  >
                    {{ t('vde.workspace.mode.standard') }}
                  </Button>
                </div>
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { AuthApiService } from "@/api/generated/services/AuthApiService";
import { useAuthStore } from "@/features/identity/composables/useAuthStore";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "./LanguageSwitcher.vue";

const { state, hasPermission, hasAnyPermission } = useAuthStore();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const mobileMenuOpen = ref(false);
const templatesDropdownOpen = ref(false);
const settingsDropdownOpen = ref(false);
const isAuthenticated = computed(() => Boolean(state.value.userId));
const viewToggleTarget = computed(() => {
  if (route.name === "aas-view") {
    const aasIdentifier = route.params.aasIdentifier;
    return typeof aasIdentifier === "string" && aasIdentifier.length > 0
      ? { name: "aas-view", params: { aasIdentifier } }
      : null;
  }

  if (route.name === "submodel-template-detail") {
    const submodelIdentifier = route.params.submodelIdentifier;
    return typeof submodelIdentifier === "string" && submodelIdentifier.length > 0
      ? { name: "submodel-template-detail", params: { submodelIdentifier } }
      : null;
  }

  return null;
});
const isDetailDisplay = computed(() => route.query.display === "detail");
const showViewToggle = computed(() => viewToggleTarget.value !== null);

function setDisplayMode(mode: "detail" | "view"): void {
  const target = viewToggleTarget.value;
  if (!target) {
    return;
  }

  const currentMode: "detail" | "view" = isDetailDisplay.value ? "detail" : "view";
  if (currentMode === mode) {
    return;
  }

  const nextQuery: Record<string, unknown> = { ...route.query };
  if (mode === "detail") {
    nextQuery.display = "detail";
  } else {
    delete nextQuery.display;
  }

  router.push({
    name: target.name,
    params: target.params,
    query: nextQuery,
    hash: route.hash,
  });
}

function toggleTemplatesDropdown(): void {
  templatesDropdownOpen.value = !templatesDropdownOpen.value;
  settingsDropdownOpen.value = false;
}

function toggleSettingsDropdown(): void {
  settingsDropdownOpen.value = !settingsDropdownOpen.value;
  templatesDropdownOpen.value = false;
}

function onDocumentClick(): void {
  templatesDropdownOpen.value = false;
  settingsDropdownOpen.value = false;
}

async function handleLogout(): Promise<void> {
  try {
    await AuthApiService.postApiAuthLogout();
  } catch {
    // Even if the API call fails, redirect to sign-in
  }
  window.location.href = "/signin";
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
});

onUnmounted(() => {
  document.removeEventListener("click", onDocumentClick);
});

watch(() => route.path, () => {
  templatesDropdownOpen.value = false;
  settingsDropdownOpen.value = false;
  mobileMenuOpen.value = false;
});
</script>
